import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Wallet, ArrowRight, Loader2, Download } from 'lucide-react';
import { toast } from 'sonner';
import { accountingAPI } from '@/lib/api';

// Phase 27.21 — Recent Payouts tile.
// Compact card that renders the recipient's most recent paid payouts so
// agents (and agency principals) see their earnings at a glance on the
// dashboard. Reuses `recipientPaymentHistory` (Phase 27.19 self-service
// authorization) and lets the user download the paid statement PDF
// directly from the tile.
//
// Props:
//   recipientType : 'agent' | 'agency' | 'subagency'
//   recipientId   : NPN / agency_id / user_id
//   profileHref   : optional deep link to the "See all" full-history view
//   limit         : how many recent payouts to render (default 5)

function money(v) {
  const n = Number(v || 0);
  return `${n < 0 ? '-$' : '$'}${Math.abs(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

export default function RecentPayoutsWidget({
  recipientType,
  recipientId,
  profileHref,
  limit = 5,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  const load = useCallback(async () => {
    if (!recipientType || !recipientId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await accountingAPI.recipientPaymentHistory(
        recipientType,
        recipientId,
        { limit: 20 },
      );
      // Show paid transactions only; the widget is intentionally
      // celebratory — failed/zero-net rows still surface in the full
      // Payment History card on the profile page.
      const paid = (data.items || []).filter((t) => t.status === 'paid');
      setRows(paid.slice(0, limit));
    } catch {
      /* silent — permission-denied or empty history */
    } finally {
      setLoading(false);
    }
  }, [recipientType, recipientId, limit]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDownloadPdf = async (row) => {
    if (!row?.statement_id) return;
    setDownloadingId(row.transaction_id);
    try {
      const res = await accountingAPI.downloadStatementPdf(row.statement_id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${row.statement_number || row.statement_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) toast.error('PDF not yet available for this statement');
      else if (status === 403) toast.error('You are not authorized to download this PDF');
      else toast.error('Download failed');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Card
      className="mb-8 animate-slideIn stagger-1 border-l-4 border-l-emerald-500"
      data-testid="recent-payouts-widget"
    >
      <CardHeader className="flex flex-row items-center justify-between py-3">
        <CardTitle className="text-base font-heading flex items-center gap-2">
          <Wallet size={16} className="text-emerald-600" />
          Recent Payouts
        </CardTitle>
        {profileHref && (
          <Link to={profileHref} data-testid="recent-payouts-view-all">
            <Button variant="ghost" size="sm" className="text-xs">
              View all
              <ArrowRight size={12} className="ml-1" />
            </Button>
          </Link>
        )}
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="p-4 text-slate-500 flex items-center gap-2 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-4 text-slate-500 text-sm" data-testid="recent-payouts-empty">
            No payouts yet. Once payments are issued they&apos;ll appear here.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {rows.map((t) => (
              <div
                key={t.transaction_id}
                className="flex items-center justify-between py-2.5"
                data-testid={`recent-payout-row-${t.transaction_id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate-900">
                      {money(t.amount)}
                    </span>
                    <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300 font-normal text-[10px] px-1.5 py-0">
                      PAID
                    </Badge>
                    {t.statement_number && (
                      <span className="font-mono text-[11px] text-slate-500">
                        {t.statement_number}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {fmtDate(t.payment_date || t.executed_at)}
                    {t.method ? ` · ${t.method.toUpperCase()}` : ''}
                    {t.reference ? ` · ${t.reference}` : ''}
                  </p>
                </div>
                {t.statement_id && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 p-0 ml-2 shrink-0"
                    onClick={() => handleDownloadPdf(t)}
                    disabled={downloadingId === t.transaction_id}
                    data-testid={`recent-payout-pdf-${t.transaction_id}`}
                    title={`Download statement ${t.statement_number || ''} PDF`}
                  >
                    {downloadingId === t.transaction_id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
